import React, { useState, useEffect, useRef } from "react"
import {
  Box,
  Container,
  Grid,
  Flex,
  Heading,
  Text,
  Image,
  Link,
} from "@theme-ui/components"
import { GatsbyImage } from "gatsby-plugin-image"
import { OutboundLink } from "gatsby-plugin-google-gtag"
import { X } from "react-feather"
import { i18nContext } from "../../context/i18nContext"
import VideoStream from "../videoStream"

const formatDuration = seconds => {
  if (!seconds || seconds <= 0) return null
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = n => String(n).padStart(2, "0")
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

const MediaCollection = ({ title, description, items }) => (
  <i18nContext.Consumer>
    {t => (
      <Container sx={{ py: [4, 5] }}>
        <Heading
          as="h2"
          variant="h2"
          dir="invalid"
          sx={{ color: "dark", mt: 0, mb: [3, 4] }}
        >
          {title}
        </Heading>
        {description && (
          <Box
            sx={{
              color: "lightGrey",
              mb: [4, 5],
              "p:last-of-type": { mb: 0 },
              "strong, b": { color: "secondary" },
              img: { maxWidth: "100%", height: "auto" },
            }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
        <Grid columns={[2, 2, 2, 4]} gap={[3, 4]}>
          {items.map(item => (
            <MediaCard key={item.id} item={item} t={t} />
          ))}
        </Grid>
      </Container>
    )}
  </i18nContext.Consumer>
)

const isVideoUpload = media =>
  Boolean(media && media.video && media.video.streamingUrl)

const MediaCard = ({ item, t }) => {
  if (item.pdf) return <PdfCard item={item} label={t.download} />
  if (item.youtubeId)
    return <YoutubeVideoCard item={item} label={t.watchVideo} />
  if (isVideoUpload(item.media))
    return <VideoCard item={item} label={t.watchVideo} />
  return <PlainCard item={item} />
}

const cardShellSx = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  color: "inherit",
  textDecoration: "none",
  "&, &:hover, &:focus": { textDecoration: "none" },
  "&:hover .media-card-image": { transform: "scale(1.03)" },
}

const titleSx = {
  mb: 1,
  color: "dark",
  fontFamily: "heading",
  fontWeight: "body",
  lineHeight: 1.2,
  fontSize: [2, 3, 4],
  textDecoration: "none",
}

const imageWrapperSx = {
  position: "relative",
  overflow: "hidden",
  aspectRatio: "1 / 1",
  bg: "lightBackground",
  flexShrink: 0,
  ".gatsby-image-wrapper, img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  ".media-card-image": {
    width: "100%",
    height: "100%",
    transition: "transform 0.4s ease",
  },
}

const textBlockSx = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  mt: [2, 3],
}

const actionSx = {
  mt: "auto",
  color: "primary",
  fontSize: [1, 2],
  fontWeight: "bold",
  textDecoration: "underline",
}

const CardImage = ({ media, alt }) => {
  const thumbnailUrl = media && media.video && media.video.thumbnailUrl
  return (
    <Box className="media-card-image">
      {media && media.gatsbyImageData ? (
        <GatsbyImage image={media.gatsbyImageData} alt={alt} />
      ) : thumbnailUrl ? (
        <Image src={thumbnailUrl} alt={alt} />
      ) : media && media.url ? (
        <Image src={media.url} alt={alt} />
      ) : null}
    </Box>
  )
}

const CardText = ({ title, label }) => (
  <Box sx={textBlockSx}>
    <Text as="h3" sx={titleSx}>
      {title}
    </Text>
    {label && (
      <Text as="span" sx={actionSx}>
        {label}
      </Text>
    )}
  </Box>
)

const PdfCard = ({ item, label }) => (
  <Link
    as={OutboundLink}
    href={item.pdf.url}
    target="_blank"
    rel="noopener nofollow"
    sx={cardShellSx}
  >
    <Box sx={imageWrapperSx}>
      <CardImage media={item.media} alt={item.title} />
      <Flex
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: ["48px", "64px"],
          height: ["48px", "64px"],
          alignItems: "center",
          justifyContent: "center",
          bg: "primary",
          color: "light",
        }}
      >
        <PdfDocIcon />
      </Flex>
    </Box>
    <CardText title={item.title} label={label} />
  </Link>
)

const PdfDocIcon = () => (
  <Box
    as="svg"
    viewBox="0 0 45 60"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ width: ["22px", "29px"], height: ["29px", "39px"] }}
    aria-hidden="true"
  >
    <path
      d="M44.3534 11.5246C44.3534 11.5097 44.3521 11.4949 44.3507 11.48C44.3507 11.4746 44.3507 11.4692 44.3507 11.4625C44.3453 11.376 44.3264 11.2923 44.2967 11.2112C44.2967 11.2112 44.2967 11.2112 44.2967 11.2099C44.2494 11.0829 44.1751 10.9668 44.0765 10.8709L33.677 0.842762L33.0665 0.253904C33.0665 0.253904 33.0638 0.251203 33.0625 0.249852C33.0355 0.224191 33.0057 0.19988 32.976 0.178271C32.9666 0.171518 32.9585 0.164765 32.949 0.159363C32.918 0.137753 32.8869 0.118845 32.8531 0.101287C32.8464 0.0972354 32.8396 0.0945343 32.8315 0.0918331C32.8032 0.0783272 32.7748 0.0661719 32.7451 0.0553671C32.7343 0.0513154 32.7221 0.0472636 32.7113 0.0432118C32.6762 0.0324071 32.6411 0.022953 32.6046 0.0162C32.5965 0.0148494 32.5898 0.0134988 32.5817 0.0134988C32.5506 0.00809645 32.5195 0.00539527 32.4885 0.00269409C32.4777 0.00269409 32.4655 0.0013435 32.4547 0.0013435C32.4479 0.0013435 32.4412 0.0013435 32.4344 0.0013435H4.20169C1.88407 -7.09225e-06 0 1.88542 0 4.20303V55.7956C0 58.1132 1.88407 59.9986 4.20169 59.9986H40.1504C42.468 59.9986 44.3534 58.1132 44.3534 55.7956V11.5246ZM41.193 10.6183H33.3447V3.04828L41.193 10.6183ZM40.1504 58.1821H4.20169C2.88621 58.1821 1.81654 57.1111 1.81654 55.7956V4.20303C1.81654 2.88756 2.88621 1.81789 4.20169 1.81789H31.5268V11.5273C31.5268 12.0297 31.9334 12.4362 32.4358 12.4362H42.5355V55.7956C42.5355 57.1111 41.4658 58.1821 40.1504 58.1821Z"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
    />
    <path
      d="M21.6066 36.285C21.6904 36.3701 21.7917 36.439 21.9065 36.4862C22.0186 36.5335 22.1374 36.5565 22.2549 36.5565C22.2927 36.5565 22.3292 36.5538 22.367 36.5497C22.4116 36.5443 22.4575 36.5349 22.4994 36.5227C22.6439 36.4808 22.7789 36.4038 22.891 36.2931L31.0121 28.1693C31.1891 27.9924 31.2782 27.7601 31.2782 27.5264C31.2782 27.2941 31.1891 27.0618 31.0121 26.8835C30.6569 26.5283 30.0802 26.5283 29.7264 26.8835L23.1571 33.4542V13.6018C23.1571 13.0994 22.7492 12.6929 22.2482 12.6929C21.7457 12.6929 21.3392 13.1007 21.3392 13.6018V33.4528L14.7699 26.8822C14.4161 26.527 13.8394 26.527 13.4842 26.8822C13.129 27.2374 13.129 27.8127 13.4842 28.1679L21.5999 36.285H21.6066Z"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
    />
    <path
      d="M35.5554 45.4906H8.79615C8.29373 45.4906 7.88721 45.8971 7.88721 46.3995C7.88721 46.902 8.29508 47.3071 8.79615 47.3071H35.5554C36.0578 47.3071 36.4644 46.9006 36.4644 46.3995C36.4644 45.8971 36.0578 45.4906 35.5554 45.4906Z"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
    />
  </Box>
)

const PlayBadge = () => (
  <Flex
    sx={{
      width: ["48px", "60px"],
      height: ["48px", "60px"],
      borderRadius: "50%",
      bg: "primary",
      color: "light",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
    }}
  >
    <Box
      as="svg"
      viewBox="0 0 16 16"
      sx={{ width: "16px", height: "16px", ml: "2px" }}
      aria-hidden="true"
    >
      <path
        d="M3 1.5v13l11-6.5L3 1.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </Box>
  </Flex>
)

const VideoCard = ({ item, label }) => {
  const [open, setOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const playerRef = useRef(null)
  const videoMeta = item.media && item.media.video
  const duration = videoMeta && formatDuration(videoMeta.duration)
  const streamingUrl = videoMeta && videoMeta.streamingUrl
  const fallbackUrl = item.media && item.media.url
  const src = streamingUrl || fallbackUrl

  useEffect(() => {
    if (!open) {
      setLoaded(false)
      return undefined
    }
    const wrapper = playerRef.current
    if (!wrapper) return undefined
    const video = wrapper.querySelector("video")
    if (!video) return undefined
    if (video.readyState >= 3) {
      setLoaded(true)
      return undefined
    }
    const handleReady = () => setLoaded(true)
    video.addEventListener("canplay", handleReady)
    video.addEventListener("playing", handleReady)
    return () => {
      video.removeEventListener("canplay", handleReady)
      video.removeEventListener("playing", handleReady)
    }
  }, [open])

  return (
    <>
      <Box
        as="button"
        type="button"
        onClick={() => setOpen(true)}
        sx={{
          ...cardShellSx,
          width: "100%",
          textAlign: "left",
          appearance: "none",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          font: "inherit",
        }}
      >
        <Box sx={imageWrapperSx}>
          <CardImage media={item.media} alt={item.title} />
          <Flex
            sx={{
              position: "absolute",
              inset: 0,
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <PlayBadge />
          </Flex>
          {duration && (
            <Box
              sx={{
                position: "absolute",
                right: 3,
                bottom: 3,
                bg: "primary",
                color: "light",
                fontSize: 1,
                fontWeight: "bold",
                px: 2,
                py: 1,
                borderRadius: "4px",
              }}
            >
              {duration}
            </Box>
          )}
        </Box>
        <CardText title={item.title} label={label} />
      </Box>
      {open && (
        <VideoModal loaded={loaded} onClose={() => setOpen(false)}>
          <Box ref={playerRef} sx={{ width: "100%", height: "100%" }}>
            {src && <VideoStream videoUrl={src} aspectRatio="16 / 9" />}
          </Box>
        </VideoModal>
      )}
    </>
  )
}

const YOUTUBE_THUMBNAIL_VARIANTS = ["maxresdefault", "mqdefault"]

const YoutubeThumbnail = ({ id, alt }) => {
  const fallback = `https://img.youtube.com/vi/${id}/${YOUTUBE_THUMBNAIL_VARIANTS[
    YOUTUBE_THUMBNAIL_VARIANTS.length - 1
  ]}.jpg`
  const [src, setSrc] = useState(fallback)

  useEffect(() => {
    let cancelled = false
    const tryNext = i => {
      if (i >= YOUTUBE_THUMBNAIL_VARIANTS.length) return
      const candidate = `https://img.youtube.com/vi/${id}/${YOUTUBE_THUMBNAIL_VARIANTS[i]}.jpg`
      const probe = new window.Image()
      probe.onload = () => {
        if (cancelled) return
        // YouTube returns a 120x90 grey placeholder for missing thumbnails
        // with HTTP 200, so onload fires for invalid URLs. Filter by size.
        if (probe.naturalWidth > 120) setSrc(candidate)
        else tryNext(i + 1)
      }
      probe.onerror = () => {
        if (!cancelled) tryNext(i + 1)
      }
      probe.src = candidate
    }
    tryNext(0)
    return () => {
      cancelled = true
    }
  }, [id])

  return <Image src={src} alt={alt} />
}

const YoutubeVideoCard = ({ item, label }) => {
  const [open, setOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const id = item.youtubeId
  const embedUrl = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`

  return (
    <>
      <Box
        as="button"
        type="button"
        onClick={() => {
          setLoaded(false)
          setOpen(true)
        }}
        sx={{
          ...cardShellSx,
          width: "100%",
          textAlign: "left",
          appearance: "none",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          font: "inherit",
        }}
      >
        <Box sx={imageWrapperSx}>
          {item.media && (item.media.gatsbyImageData || item.media.url) ? (
            <CardImage media={item.media} alt={item.title} />
          ) : (
            <Box className="media-card-image">
              <YoutubeThumbnail id={id} alt={item.title} />
            </Box>
          )}
          <Flex
            sx={{
              position: "absolute",
              inset: 0,
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <PlayBadge />
          </Flex>
        </Box>
        <CardText title={item.title} label={label} />
      </Box>
      {open && (
        <VideoModal loaded={loaded} onClose={() => setOpen(false)}>
          <Box
            as="iframe"
            src={embedUrl}
            title={item.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            onLoad={() => setLoaded(true)}
            sx={{
              width: "100%",
              height: "100%",
              border: "none",
              display: "block",
            }}
          />
        </VideoModal>
      )}
    </>
  )
}

const PlainCard = ({ item }) => (
  <Box sx={cardShellSx}>
    <Box sx={imageWrapperSx}>
      <CardImage media={item.media} alt={item.title} />
    </Box>
    <CardText title={item.title} />
  </Box>
)

const VideoModal = ({ loaded, onClose, children }) => {
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  return (
    <Flex
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        bg: "rgba(0, 0, 0, 0.85)",
        zIndex: 10000,
        alignItems: "center",
        justifyContent: "center",
        p: [3, 4],
      }}
    >
      <Box
        onClick={e => e.stopPropagation()}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "1024px",
          aspectRatio: "16 / 9",
        }}
      >
        <Box
          as="button"
          type="button"
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: [2, -40],
            right: [2, 0],
            zIndex: 1,
            appearance: "none",
            background: "none",
            border: "none",
            color: "light",
            cursor: "pointer",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <X size={32} />
        </Box>
        <Box sx={{ width: "100%", height: "100%" }}>{children}</Box>
        {!loaded && (
          <Flex
            sx={{
              position: "absolute",
              inset: 0,
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              bg: "dark",
            }}
            aria-hidden="true"
          >
            <Spinner />
          </Flex>
        )}
      </Box>
    </Flex>
  )
}

const Spinner = () => (
  <Box
    sx={{
      width: "56px",
      height: "56px",
      borderRadius: "50%",
      border: "4px solid rgba(255, 255, 255, 0.2)",
      borderTopColor: "primary",
      animation: "media-collection-spin 0.8s linear infinite",
      "@keyframes media-collection-spin": {
        to: { transform: "rotate(360deg)" },
      },
    }}
  />
)

export default MediaCollection
